<!-- Page Header -->
<div class="d-flex justify-content-between align-items-start mb-4">
  <div>
    <nav aria-label="breadcrumb">
      <ol class="breadcrumb mb-1">
        <li class="breadcrumb-item"><a routerLink="/drones">Drones</a></li>
        <li class="breadcrumb-item active">{{ isEditMode ? 'Edit Drone' : 'Add Drone' }}</li>
      </ol>
    </nav>
    <h1 class="h2 mb-0 fw-bold">{{ isEditMode ? 'Edit Drone' : 'Add Drone' }}</h1>
  </div>
</div>

<form [formGroup]="form" (ngSubmit)="onSubmit()">
  <div class="row g-4">
    <!-- Left Column - Main Form -->
    <div class="col-lg-8">
      <!-- Basic Information -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="icon-drone me-2"></i>Basic Information</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-12">
              <label class="form-label">Drone Name <span class="text-danger">*</span></label>
              <input type="text" class="form-control" formControlName="name"
                     placeholder="e.g., Farm Sprayer #1"
                     [class.is-invalid]="isFieldInvalid('name')">
              <div class="invalid-feedback">Please enter a drone name (at least 2 characters)</div>
            </div>
            <div class="col-md-6">
              <label class="form-label">Manufacturer <span class="text-danger">*</span></label>
              <select class="form-select" formControlName="manufacturerId"
                      [class.is-invalid]="isFieldInvalid('manufacturerId')"
                      (change)="onManufacturerChange($event)">
                <option value="">Select manufacturer...</option>
                <option *ngFor="let mfr of manufacturers()" [value]="mfr.id">{{ mfr.name }}</option>
              </select>
              <div class="invalid-feedback">Please select a manufacturer</div>
            </div>
            <div class="col-md-6">
              <label class="form-label">Model <span class="text-danger">*</span></label>
              <select class="form-select" formControlName="modelId"
                      [class.is-invalid]="isFieldInvalid('modelId')">
                <option value="">{{ selectedManufacturerId() ? 'Select model...' : 'Select manufacturer first' }}</option>
                <option *ngFor="let model of filteredModels()" [value]="model.id">
                  {{ model.modelName }}
                  <ng-container *ngIf="model.maxTakeoffWeightLbs"> ({{ model.maxTakeoffWeightLbs }} lbs)</ng-container>
                </option>
              </select>
              <div class="invalid-feedback">Please select a model</div>
            </div>
            <div class="col-12">
              <label class="form-label">Serial Number <span class="text-danger">*</span></label>
              <input type="text" class="form-control" formControlName="serialNumber"
                     placeholder="e.g., DJI-AT40-2024-001"
                     [class.is-invalid]="isFieldInvalid('serialNumber')">
              <div class="invalid-feedback">Please enter the drone's serial number</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Registration -->
      <div class="card mb-4">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-card-text me-2"></i>Registration</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label">FAA Registration Number (N-Number)</label>
              <input type="text" class="form-control" formControlName="registrationNumber"
                     placeholder="e.g., N12345">
              <div class="form-text">Optional. Required for drones over 55 lbs.</div>
            </div>
            <div class="col-md-6">
              <label class="form-label">Remote ID</label>
              <input type="text" class="form-control" formControlName="remoteId"
                     placeholder="e.g., RID-ORG-001">
              <div class="form-text">Optional. Remote ID module identifier.</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Technical Details -->
      <div class="card mb-4 mb-lg-0">
        <div class="card-header p-3">
          <h5 class="mb-0"><i class="bi bi-gear me-2"></i>Technical Details</h5>
        </div>
        <div class="card-body">
          <div class="row g-3">
            <div class="col-md-6">
              <label class="form-label">Firmware Version</label>
              <input type="text" class="form-control" formControlName="firmwareVersion"
                     placeholder="e.g., v4.2.1">
            </div>
            <div class="col-md-6">
              <label class="form-label">Status</label>
              <select class="form-select" formControlName="status">
                <option *ngFor="let status of statusOptions" [value]="status.value">{{ status.label }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Column - Sidebar -->
    <div class="col-lg-4">
      <div class="sticky-top sticky-sidebar">
      <!-- Tips Card -->
      <div class="card mb-4">
        <div class="card-body">
          <h6 class="d-flex align-items-center gap-2 mb-3">
            <i class="bi bi-lightbulb text-warning"></i>
            Tips
          </h6>
          <ul class="small text-muted mb-0 ps-3">
            <li class="mb-2">Only organization-owned drones can be added here. ADN-leased drones are managed by American Drone Network.</li>
            <li class="mb-2">The serial number should match the manufacturer's serial exactly.</li>
            <li class="mb-2">FAA registration is required for drones weighing over 55 lbs at takeoff.</li>
            <li>You can track maintenance records after adding the drone.</li>
          </ul>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="card">
        <div class="card-header p-3">
          <h5 class="mb-0">Summary</h5>
        </div>
        <div class="card-body">
          <div class="summary-item" *ngIf="form.get('name')?.value">
            <div class="summary-label text-muted">Name</div>
            <div class="summary-value">{{ form.get('name')?.value }}</div>
          </div>
          <div class="summary-item" *ngIf="getSelectedManufacturerName()">
            <div class="summary-label text-muted">Manufacturer</div>
            <div class="summary-value">{{ getSelectedManufacturerName() }}</div>
          </div>
          <div class="summary-item" *ngIf="getSelectedModelName()">
            <div class="summary-label text-muted">Model</div>
            <div class="summary-value">{{ getSelectedModelName() }}</div>
          </div>
          <div class="summary-item" *ngIf="form.get('serialNumber')?.value">
            <div class="summary-label text-muted">Serial Number</div>
            <div class="summary-value">
              <code class="small">{{ form.get('serialNumber')?.value }}</code>
            </div>
          </div>

          <hr class="my-3">

          <div class="d-grid gap-2">
            <button type="submit" class="btn btn-primary" [disabled]="isSaving">
              <ng-container *ngIf="!isSaving">
                <i class="bi bi-check-lg me-2"></i>{{ isEditMode ? 'Update Drone' : 'Add Drone' }}
              </ng-container>
              <ng-container *ngIf="isSaving">
                <span class="spinner-border spinner-border-sm me-2" role="status"></span>
                Saving...
              </ng-container>
            </button>
            <button type="button" class="btn btn-outline-secondary" (click)="onCancel()">
              Cancel
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  </div>
</form>
